import type {ComponentProps, FC} from 'react';
import type {PortableTextComponentProps, PortableTextMarkComponentProps} from '@portabletext/react';
import {PortableText as PortableTextReact, toPlainText} from '@portabletext/react';

import {slugifyHeading} from '@/modules/finkn/shared/utils';
import {BulletList} from '@/modules/foundation/components/portable-text/bullet-list';
import {
	Heading2,
	Heading3,
	Heading4,
	Heading5,
	Heading6,
} from '@/modules/foundation/components/portable-text/headings';
import {Normal} from '@/modules/foundation/components/portable-text/normal';
import {NumberList} from '@/modules/foundation/components/portable-text/number-list';
import {PortableImage} from '@/modules/foundation/components/portable-text/portable-image';
import {imageWebFromImageWebSchema} from '@/modules/foundation/sanity/image/query';
import type {LinkFragment} from '@/modules/foundation/sanity/link/query';
import {cn} from '@/modules/foundation/shared/tailwind';
import type {ImageWebFull} from '@/sanity/schemas/types';

import {Blockquote} from '../../components/portable-text/blockquote';

import type {AccordionListFragment} from './accordion-container/query';
import type {BlockquoteFragment} from './blockquote/query';
import {CallToActionLineContainer} from './call-to-action-line-container/CallToActionLine';
import type {CallToActionLineFragment} from './call-to-action-line-container/query';
import type {ColorBlockFragment} from './color-block/query';
import type {InlineBannerFragment} from './inline-banner/query';
import {AccordionListContainer} from './accordion-container';
import {ColorBlock} from './color-block';
import {InlineBannerContainer} from './inline-banner';
import {PortableLinkContainer} from './portable-link-container';

/**
 * Override class names for portable text components
 */
export type PortableTextOverrides = {
	p?: string;
	h2?: string;
	h3?: string;
	h4?: string;
	h5?: string;
	h6?: string;
	ul?: string;
	ol?: string;
	callToActionLine?: string;
};

type SanityPortableTextProps = ComponentProps<typeof PortableTextReact>;

interface Props extends SanityPortableTextProps {
	overrideClassNames?: PortableTextOverrides;
	anchorHeadings?: boolean;
	theme?: 'light' | 'dark';
}

export const PortableText: FC<Props> = ({value, overrideClassNames, anchorHeadings, theme}) => {
	return (
		<PortableTextReact
			value={value}
			components={{
				block: {
					normal: ({children}) => (
						<Normal className={overrideClassNames?.p}>{children}</Normal>
					),
					h2: ({children, value}) => {
						const heading = toPlainText(value);
						const id = slugifyHeading(heading);
						return (
							<Heading2
								{...(anchorHeadings ? {id} : {})}
								className={cn(['scroll-m-20'], overrideClassNames?.h2)}
							>
								{children}
							</Heading2>
						);
					},
					h3: ({children}) => (
						<Heading3 className={cn([overrideClassNames?.h3])}>{children}</Heading3>
					),
					h4: ({children}) => (
						<Heading4 className={cn([overrideClassNames?.h4])}>{children}</Heading4>
					),
					h5: ({children}) => (
						<Heading5 className={cn([overrideClassNames?.h5])}>{children}</Heading5>
					),
					h6: ({children}) => (
						<Heading6 className={cn([overrideClassNames?.h6])}>{children}</Heading6>
					),
				},
				marks: {
					link: ({
						value,
						children,
					}: PortableTextMarkComponentProps<LinkFragment & {_type: 'link'}>) => {
						if (!value) {
							return null;
						}

						return (
							<PortableLinkContainer value={value} theme={theme}>
								{children}
							</PortableLinkContainer>
						);
					},
					linkAnnotation: () => null,
				},
				list: {
					bullet: ({children}) => (
						<BulletList className={overrideClassNames?.ul}>{children}</BulletList>
					),
					number: ({children}) => (
						<NumberList className={overrideClassNames?.ol}>{children}</NumberList>
					),
				},
				types: {
					callToActionLine: ({
						value,
					}: PortableTextComponentProps<CallToActionLineFragment>) => (
						<CallToActionLineContainer
							value={value}
							className={overrideClassNames?.callToActionLine}
							theme={theme}
						/>
					),
					imageWebFull: ({value}: PortableTextComponentProps<ImageWebFull>) => {
						if (!value.asset?._ref) {
							return null;
						}
						return <PortableImage image={imageWebFromImageWebSchema(value)} />;
					},
					colorBlock: ({value}: PortableTextComponentProps<ColorBlockFragment>) => {
						return (
							<ColorBlock
								themeColor={value.themeColor}
								content={value.content ?? []}
							/>
						);
					},
					blockquote: ({value}: PortableTextComponentProps<BlockquoteFragment>) => {
						return <Blockquote quote={value.quote} source={value.source} />;
					},
					accordionList: ({value}: PortableTextComponentProps<AccordionListFragment>) => {
						return <AccordionListContainer {...value} />;
					},
					inlineBanner: ({value}: PortableTextComponentProps<InlineBannerFragment>) => {
						return <InlineBannerContainer data={value} />;
					},
				},
			}}
		/>
	);
};
